import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { useAuth } from "@/contexts/AuthContext";
import { Modal, Field } from "./Customers";
import { Check, X, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* /admin/quotes — Super admin quote-management console.
   Lists every quote-request across all workspaces, lets the super-admin
   set an amount + currency (status -> quoted) or decline.  Once quoted, the
   workspace admin sees the PayPal card-fields on /billing/upgrade and can pay.
*/
export default function QuotesAdmin() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);  // quote being quoted

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/admin/quote-requests");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load quotes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decline = async (q) => {
    if (!window.confirm(`Decline quote from ${q.contact_name}?`)) return;
    try {
      await api.post(`/billing/admin/quote-requests/${q.id}/decline`);
      toast.success("Quote declined");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decline failed"); }
  };

  const isSuper = user?.role === "super_admin";

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="quotes-admin-page">
      <PageHeader
        title="Quote management"
        subtitle={isSuper
          ? "Every pending quote request across all workspaces. Set an annual price and the workspace admin can pay via PayPal."
          : "Your workspace's quote requests."}
      />
      <div className="mt-8 border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="quotes-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr>
              <Th>Workspace</Th>
              <Th>Contact</Th>
              <Th>Industry / Users</Th>
              <Th>Country</Th>
              <Th>Created</Th>
              <Th>Status</Th>
              <Th>Amount</Th>
              {isSuper && <Th>Actions</Th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isSuper ? 8 : 7} className="p-8 text-center text-[#495057]"><Loader2 size={14} className="inline animate-spin mr-2" />Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={isSuper ? 8 : 7} className="p-8 text-center text-[#495057]">No quote requests yet.</td></tr>}
            {rows.map((q, i) => (
              <tr key={q.id} data-testid={`quote-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                <Td>
                  <div className="font-medium">{q.tenant_name || "—"}</div>
                  <div className="text-xs text-[#495057] font-mono">{q.tenant_slug}</div>
                </Td>
                <Td>
                  <div className="font-medium">{q.contact_name}</div>
                  <div className="text-xs text-[#495057]">{q.contact_email}</div>
                </Td>
                <Td className="text-xs text-[#495057]">{q.industry || "?"} · {q.estimated_users ?? "?"} users · {q.company_size || "?"}</Td>
                <Td className="text-xs text-[#495057]">{q.country || "—"}</Td>
                <Td className="text-xs text-[#495057] whitespace-nowrap">{q.created_at?.slice(0, 10)}</Td>
                <Td><StatusPill status={q.status} /></Td>
                <Td className="font-mono text-xs">
                  {q.quoted_amount ? `${q.quoted_currency} ${q.quoted_amount.toFixed(2)}` : "—"}
                </Td>
                {isSuper && (
                  <Td>
                    {q.status !== "paid" && q.status !== "declined" && (
                      <div className="flex gap-2">
                        <button onClick={() => setActive(q)} data-testid={`quote-set-amount-${i}`}
                          className="inline-flex items-center gap-1 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-2.5 py-1 text-xs">
                          <DollarSign size={12} /> {q.status === "quoted" ? "Re-quote" : "Quote"}
                        </button>
                        <button onClick={() => decline(q)} data-testid={`quote-decline-${i}`}
                          className="inline-flex items-center gap-1 border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white px-2.5 py-1 text-xs">
                          <X size={12} /> Decline
                        </button>
                      </div>
                    )}
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <QuoteAmountModal
          quote={active}
          onClose={() => setActive(null)}
          onSaved={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    paid:     "bg-[#0B7A75]/10 text-[#0B7A75]",
    quoted:   "bg-[#FFB703]/15 text-[#0A0A0A]",
    declined: "bg-[#D90429]/10 text-[#D90429]",
    new:      "bg-[#DEE2E6] text-[#495057]",
  }[status] || "bg-[#DEE2E6] text-[#495057]";
  return <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${cls}`} data-testid={`status-${status}`}>{status}</span>;
}

function Th({ children }) { return <th className="text-left p-3 font-medium">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`p-3 align-top ${className}`}>{children}</td>; }

function QuoteAmountModal({ quote, onClose, onSaved }) {
  const [amount, setAmount] = useState(quote.quoted_amount?.toString() || "");
  const [currency, setCurrency] = useState(quote.quoted_currency || "USD");
  const [notes, setNotes] = useState(quote.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const a = parseFloat(amount);
      if (!a || a <= 0) throw new Error("Enter a valid amount");
      await api.post(`/billing/admin/quote-requests/${quote.id}/quote`, {
        amount: a, currency, notes: notes || null,
      });
      toast.success(`Quote sent: ${currency} ${a.toFixed(2)}`);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Quote ${quote.tenant_name || quote.tenant_slug}`} onClose={onClose} testid="quote-amount-modal">
      <form onSubmit={save} className="space-y-4" data-testid="quote-amount-form">
        <div className="text-xs text-[#495057] border border-[#DEE2E6] bg-[#F8F9FA] p-3">
          <strong>{quote.contact_name}</strong> · {quote.contact_email}<br />
          {quote.industry} · {quote.estimated_users} users · {quote.country}
          {quote.notes && <div className="mt-2 italic">&ldquo;{quote.notes}&rdquo;</div>}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Annual amount" required value={amount} onChange={setAmount} testid="quote-amount-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Currency</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="quote-amount-currency"
              className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none">
              {["USD", "EUR", "GBP", "GHS", "ZAR", "KES", "NGN", "INR"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Internal notes (optional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-amount-notes" rows={2}
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </div>
        {err && <div className="text-sm text-[#D90429]" data-testid="quote-amount-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} data-testid="quote-amount-cancel" className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="quote-amount-save"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2 text-sm font-medium disabled:opacity-60">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Check size={14} /> Send quote</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}
